<template>
  <div class="w-full max-w-[440px] z-10 transition-all duration-700 ease-in-out">
    <!-- Main Card -->
    <div class="bg-slate-900/40 backdrop-blur-2xl border border-slate-800 rounded-[2.5rem] shadow-2xl shadow-black/80 p-10 overflow-hidden relative group">
      
      <!-- Animated Corner Accents -->
      <div :class="cn('absolute top-0 left-0 w-12 h-12 border-t-2 border-l-2 rounded-tl-[2.5rem] transition-colors duration-500', accentBorderClass)"></div>
      <div :class="cn('absolute top-0 right-0 w-12 h-12 border-t-2 border-r-2 rounded-tr-[2.5rem] transition-colors duration-500', accentBorderClass)"></div>
      <div :class="cn('absolute bottom-0 left-0 w-12 h-12 border-b-2 border-l-2 rounded-bl-[2.5rem] transition-colors duration-500', accentBorderClass)"></div>
      <div :class="cn('absolute bottom-0 right-0 w-12 h-12 border-b-2 border-r-2 rounded-br-[2.5rem] transition-colors duration-500', accentBorderClass)"></div>

      <div class="text-center space-y-1 mb-10 mt-2">
        <div :class="cn('inline-flex w-16 h-16 rounded-2xl items-center justify-center mb-4 border transition-all duration-500 shadow-lg shadow-black/20', iconContainerClass)">
           <slot name="logo">
             <Layout class="w-8 h-8" />
           </slot>
        </div>
        <h1 class="text-3xl font-black text-white tracking-widest uppercase">
          {{ appName.split(/(?=[A-Z])/).join(' ') }}<slot name="title-suffix"></slot>
        </h1>
        <div class="flex items-center justify-center gap-2">
          <span class="h-px w-6 bg-slate-800"></span>
          <p class="text-slate-500 text-[10px] font-black uppercase tracking-[0.3em]">{{ subtitle }}</p>
          <span class="h-px w-6 bg-slate-800"></span>
        </div>
      </div>

      <!-- Form Switcher -->
      <div role="tablist" class="flex p-1 bg-slate-950/50 rounded-xl mb-8 border border-slate-800/50" aria-label="Authentification">
        <button role="tab" 
                :aria-selected="!isRegister"
                aria-controls="auth-form"
                @click="isRegister = false" 
                class="flex-1 py-2 text-[10px] font-black uppercase tracking-widest rounded-lg transition-all focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                :class="!isRegister ? cn('text-white shadow-lg', accentBgClass) : 'text-slate-500 hover:text-slate-300'">
          Connexion
        </button>
        <button role="tab"
                :aria-selected="isRegister"
                aria-controls="auth-form"
                @click="isRegister = true" 
                class="flex-1 py-2 text-[10px] font-black uppercase tracking-widest rounded-lg transition-all focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                :class="isRegister ? cn('text-white shadow-lg', accentBgClass) : 'text-slate-500 hover:text-slate-300'">
          Inscription
        </button>
      </div>

      <form id="auth-form" @submit.prevent="handleSubmit" class="space-y-6">
        <div class="space-y-4">
          <div class="space-y-2">
            <label for="email" class="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-1">Adresse Email</label>
            <div class="relative group">
              <div :class="cn('absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-600 transition-colors', focusIconClass)">
                <Mail class="w-full h-full" />
              </div>
              <input id="email" v-model="email" type="email" placeholder="votre@email.com" required
                     class="w-full bg-slate-950/80 border border-slate-800 rounded-2xl py-4 pl-12 pr-4 text-white placeholder:text-slate-700 focus:outline-none focus:ring-2 transition-all font-bold tracking-tight"
                     :class="focusRingClass" />
            </div>
          </div>

          <div class="space-y-2">
            <label for="password" class="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-1">Mot de passe</label>
            <div class="relative group">
              <div :class="cn('absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-600 transition-colors', focusIconClass)">
                <Lock class="w-full h-full" />
              </div>
              <input id="password" v-model="password" type="password" placeholder="••••••••••••" required
                     class="w-full bg-slate-950/80 border border-slate-800 rounded-2xl py-4 pl-12 pr-4 text-white placeholder:text-slate-700 focus:outline-none focus:ring-2 transition-all font-bold"
                     :class="focusRingClass" />
            </div>
          </div>
        </div>

        <div v-if="error" role="alert" class="text-rose-400 text-[10px] font-black uppercase tracking-widest bg-rose-500/10 p-4 rounded-xl border border-rose-500/20 flex items-center gap-3 animate-shake">
          <AlertCircle class="w-4 h-4 shrink-0" />
          <span>ERREUR : {{ error }}</span>
        </div>

        <button type="submit" 
                :disabled="isLoading"
                :aria-busy="isLoading"
                :aria-label="isRegister ? 'S\'inscrire' : 'Se connecter'"
                :class="cn('w-full h-14 text-white font-black uppercase tracking-[0.2em] rounded-2xl transition-all duration-500 active:scale-95 disabled:opacity-50 disabled:active:scale-100 flex items-center justify-center gap-3 group relative overflow-hidden', accentBgClass)">
          <div class="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
          <span v-if="!isLoading" class="text-xs">{{ isRegister ? 'Créer un compte' : 'Se connecter' }}</span>
          <RefreshCw v-else class="w-5 h-5 animate-spin" />
          <ChevronRight v-if="!isLoading" class="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </button>
      </form>

      <Transition name="fade">
        <div v-if="!isRegister" class="mt-8 text-center text-[10px] font-black uppercase tracking-[0.1em] text-slate-600 italic">
          Accès sécurisé Codifyo Ecosystem
        </div>
      </Transition>
    </div>

    <div class="mt-8 flex justify-between px-4 opacity-30 select-none">
      <div class="flex gap-4">
         <Layers class="w-4 h-4 text-slate-400" />
         <FileText class="w-4 h-4 text-slate-400" />
      </div>
      <div class="text-[8px] font-mono text-slate-400 uppercase tracking-tighter">
         System_Ready / {{ appKey.toUpperCase() }}_Sync_Ok
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { 
  Mail, Lock, AlertCircle, 
  ChevronRight, RefreshCw, Layout, 
  Layers, FileText 
} from 'lucide-vue-next'
import { cn } from '@/lib/utils'

const props = defineProps<{
  appName: string
  appKey: string
  subtitle: string
  themeColor?: 'blue' | 'indigo' | 'emerald' | 'amber' | 'rose'
  successRoute: string
}>()

const router = useRouter()
const email = ref('')
const password = ref('')
const error = ref('')
const isLoading = ref(false)
const isRegister = ref(false)

const accentBorderClass = computed(() => {
  const colors = {
    blue: 'border-blue-500/40 group-hover:border-blue-400/80',
    indigo: 'border-indigo-500/40 group-hover:border-indigo-400/80',
    emerald: 'border-emerald-500/40 group-hover:border-emerald-400/80',
    amber: 'border-amber-500/40 group-hover:border-amber-400/80',
    rose: 'border-rose-500/40 group-hover:border-rose-400/80'
  }
  return colors[props.themeColor || 'blue']
})

const iconContainerClass = computed(() => {
  const colors = {
    blue: 'bg-blue-500/10 border-blue-500/20 group-hover:bg-blue-500 text-blue-400 group-hover:text-white',
    indigo: 'bg-indigo-500/10 border-indigo-500/20 group-hover:bg-indigo-500 text-indigo-400 group-hover:text-white',
    emerald: 'bg-emerald-500/10 border-emerald-500/20 group-hover:bg-emerald-500 text-emerald-400 group-hover:text-white',
    amber: 'bg-amber-500/10 border-amber-500/20 group-hover:bg-amber-500 text-amber-400 group-hover:text-white',
    rose: 'bg-rose-500/10 border-rose-500/20 group-hover:bg-rose-500 text-rose-400 group-hover:text-white'
  }
  return colors[props.themeColor || 'blue']
})

const accentBgClass = computed(() => {
  const colors = {
    blue: 'bg-blue-600 hover:bg-blue-500',
    indigo: 'bg-indigo-600 hover:bg-indigo-500',
    emerald: 'bg-emerald-600 hover:bg-emerald-500',
    amber: 'bg-amber-600 hover:bg-amber-500',
    rose: 'bg-rose-600 hover:bg-rose-500'
  }
  return colors[props.themeColor || 'blue']
})

const focusRingClass = computed(() => {
  const colors = {
    blue: 'focus:ring-blue-500/20 focus:border-blue-500/50',
    indigo: 'focus:ring-indigo-500/20 focus:border-indigo-500/50',
    emerald: 'focus:ring-emerald-500/20 focus:border-emerald-500/50',
    amber: 'focus:ring-amber-500/20 focus:border-amber-500/50',
    rose: 'focus:ring-rose-500/20 focus:border-rose-500/50'
  }
  return colors[props.themeColor || 'blue']
})

const focusIconClass = computed(() => {
  const colors = {
    blue: 'group-focus-within:text-blue-400',
    indigo: 'group-focus-within:text-indigo-400',
    emerald: 'group-focus-within:text-emerald-400',
    amber: 'group-focus-within:text-amber-400',
    rose: 'group-focus-within:text-rose-400'
  }
  return colors[props.themeColor || 'blue']
})

const handleSubmit = () => {
    if (isRegister.value) {
        handleRegister()
    } else {
        handleLogin()
    }
}

const handleLogin = async () => {
    error.value = ''
    isLoading.value = true

    try {
        const response = await fetch('/api/login', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ 
                email: email.value, 
                password: password.value,
                app: props.appKey 
            })
        })

        const data = await response.json()

        if (response.ok) {
            localStorage.setItem('apiToken', data.token)
            router.push({ name: props.successRoute })
        } else {
            error.value = data.error || 'Identifiants invalides'
        }
    } catch (e) {
        error.value = 'Délai de connexion dépassé'
    } finally {
        isLoading.value = false
    }
}

const handleRegister = async () => {
    error.value = ''
    isLoading.value = true

    try {
        const response = await fetch('/api/register', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ 
                email: email.value, 
                password: password.value,
                app: props.appKey
            })
        })

        const data = await response.json()

        if (response.ok) {
            localStorage.setItem('apiToken', data.token)
            router.push({ name: props.successRoute })
        } else {
            error.value = data.error || 'INSCRIPTION IMPOSSIBLE'
        }
    } catch (e) {
        error.value = 'Erreur de synchronisation API'
    } finally {
        isLoading.value = false
    }
}
</script>

<style scoped>
@keyframes shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-4px); }
  75% { transform: translateX(4px); }
}
.animate-shake {
  animation: shake 0.2s ease-in-out 0s 2;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.5s ease;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}
</style>
